import {
  AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  NamesPopularityService, PopularityPoint, PopularityResponse,
} from 'src/app/services/names-popularity-service';

/* =============================================================================
   Names · "Popularity is not value".

   Every hammer-verified sale on the public Register whose name appears in the
   prevalence dataset. The cloud is drawn to a canvas (too many points for the
   DOM); the labelled standouts are real elements on top, so they stay
   selectable and readable by assistive tech.

   Three honest constraints are enforced here rather than papered over:
     · Rank is prevalence in the LIVING population, not birth fashion. A birth
       rank puts Susan 3,879th and Arlo 41st, which is the reverse of reality.
       The source counts England, so the copy says England, not Britain.
     · Prices are nominal as sold. Restating them by CPI is offered, never
       applied silently, and the basis is always named.
     · Prices are never restated by the PlateX index here: moving a past sale
       to today's market level is the valuation tool's job (owner decision
       2026-10-23), so the site shows actual prices or plain inflation only.
   ========================================================================== */

type Basis = 'nominal' | 'cpi';
type SortKey = 'rank' | 'price' | 'reg';

interface CpiFile {
  meta: { base: string; source: string; sourceUrl: string; licence: string };
  points: { year: number; value: number; partial?: boolean }[];
}

/** A point with its price restated into the active basis. */
interface PlottedPoint extends PopularityPoint {
  shownPence: number;
  approx: boolean; // no CPI figure for the sale year, so shown as sold
}

const LABEL_COUNT = 10;
/** Small first page: the table is evidence you can open, not the section itself. */
const TABLE_PAGE = 12;
/** The pair the standfirst is built on. James and Robert are 7th and 8th by
    prevalence, so "one place apart" is literally true, and the gap between
    what their plates fetched is the whole point of the section. */
const PAIR = ['James', 'Robert'];

@Component({
  selector: 'pla-names-popularity',
  templateUrl: './names-popularity.component.html',
  styleUrls: ['./names-popularity.component.scss'],
})
export class NamesPopularityComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('cloud') cloudRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('plot') plotRef?: ElementRef<HTMLElement>;

  public loading = true;
  public failed = false;
  public basis: Basis = 'nominal';
  public sortKey: SortKey = 'rank';
  public sortAsc = true;
  public tableRows = TABLE_PAGE;

  public data: PopularityResponse | null = null;
  public plotted: PlottedPoint[] = [];
  public labelled: PlottedPoint[] = [];
  public pair: PlottedPoint[] = [];
  private labelPool: PlottedPoint[] = [];

  /** axis geometry, recomputed whenever the basis or the box changes */
  public xTicks: { x: number; label: string }[] = [];
  public yTicks: { y: number; label: string }[] = [];
  public box = { w: 0, h: 0, l: 64, r: 18, t: 14, b: 42 };

  private cpi: { [year: number]: number } = {};
  private cpiLatest = 0;
  private ro: ResizeObserver | null = null;

  constructor(
    private popularityService: NamesPopularityService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.popularityService.getPopularity().subscribe(
      (res) => {
        this.data = res;
        this.loading = false;
        this.recompute();
      },
      () => {
        this.loading = false;
        this.failed = true;
      }
    );
    // the CPI series ships with the app
    this.http.get<CpiFile>('assets/market-index/ons-cpi-annual.json').subscribe(
      (f) => {
        (f.points || []).forEach((p) => (this.cpi[p.year] = p.value));
        this.cpiLatest = Math.max(...Object.keys(this.cpi).map(Number));
        this.recompute();
      },
      () => (this.cpi = {})
    );
  }

  ngAfterViewInit(): void {
    this.scheduleLayout();
  }

  /* The plot element is behind *ngIf="!loading", so it does not exist while the
     data is in flight: a @ViewChild read during the subscribe callback is still
     undefined, and an observer attached in ngAfterViewInit would bind to
     nothing. Lay out after change detection instead, and attach the observer
     the first time the element is actually there. */
  private scheduleLayout(attempt = 0): void {
    setTimeout(() => {
      const host = this.plotRef && this.plotRef.nativeElement;
      if (!host || !host.clientWidth) {
        if (attempt < 20) this.scheduleLayout(attempt + 1);
        return;
      }
      if (!this.ro && typeof ResizeObserver !== 'undefined') {
        this.ro = new ResizeObserver(() => this.layout());
        this.ro.observe(host);
      }
      this.layout();
    }, attempt === 0 ? 0 : 120);
  }

  ngOnDestroy(): void {
    if (this.ro) this.ro.disconnect();
  }

  /* =============== restatement =============== */

  /** Restate one nominal price into the active basis. */
  private restate(p: PopularityPoint): { pence: number; approx: boolean } {
    if (this.basis === 'nominal') return { pence: p.pricePence, approx: false };
    const then = this.cpi[p.year];
    const now = this.cpi[this.cpiLatest];
    if (!then || !now) return { pence: p.pricePence, approx: true };
    return { pence: Math.round(p.pricePence * (now / then)), approx: false };
  }

  public setBasis(b: Basis): void {
    if (this.basis === b) return;
    this.basis = b;
    this.recompute();
  }

  /** Short form for the chart, which rescales with the basis but no longer
      sits next to the control that changes it. */
  public get basisLabel(): string {
    return this.basis === 'nominal' ? 'as sold' : 'in today’s money';
  }

  public get basisNote(): string {
    if (this.basis === 'nominal') return 'Plotted as sold, in the money of the day.';
    return 'Restated in today’s money using the ONS consumer prices index. This removes general inflation, not the plate market.';
  }

  /* =============== geometry =============== */

  private recompute(): void {
    if (!this.data) return;
    this.plotted = this.data.points.map((p) => {
      const r = this.restate(p);
      return { ...p, shownPence: r.pence, approx: r.approx };
    });

    // label candidates: the biggest sales, plus the pair the standfirst names.
    // Which of them actually get drawn is decided in layout(), once the plot
    // box is known and overlaps can be measured.
    const bestByName = new Map<string, PlottedPoint>();
    this.plotted.forEach((p) => {
      const b = bestByName.get(p.name);
      if (!b || p.shownPence > b.shownPence) bestByName.set(p.name, p);
    });
    this.pair = PAIR.map((n) => bestByName.get(n)).filter(Boolean) as PlottedPoint[];
    const byValue = Array.from(bestByName.values()).sort((a, b) => b.shownPence - a.shownPence);
    this.labelPool = this.pair.concat(
      byValue.filter((p) => !this.pair.some((q) => q.registration === p.registration))
    );

    this.scheduleLayout();
  }

  private layout(): void {
    const host = this.plotRef && this.plotRef.nativeElement;
    if (!host || !this.plotted.length) return;
    const w = host.clientWidth;
    const h = host.clientHeight;
    if (!w || !h) return;
    this.box = { ...this.box, w, h };

    const maxP = this.plotted.reduce((m, p) => Math.max(m, p.shownPence), 0) / 100;
    // sqrt on price: the tail is a handful of sales and a linear axis buries
    // everything else against the floor
    const step = this.niceSqrtStep(maxP);
    this.yTicks = [];
    for (let i = 0; i * i * step <= maxP * 1.02; i++) {
      const v = i * i * step;
      this.yTicks.push({ y: this.yPos(v * 100), label: this.money(v * 100) });
    }
    this.xTicks = [1, 10, 100, 1000].map((r) => ({
      x: this.xPos(r),
      label: this.ordinal(r),
    }));
    this.placeLabels();
    this.drawCloud();
  }

  /** Draw a label only where it will not sit on top of one already placed. The
      dot must stay on its true coordinate, so a crowded label is dropped rather
      than nudged somewhere it would misreport the sale. */
  private placeLabels(): void {
    const placed: { x: number; y: number }[] = [];
    const out: PlottedPoint[] = [];
    for (const p of this.labelPool) {
      if (out.length >= LABEL_COUNT + this.pair.length) break;
      const x = this.xPos(p.rank);
      const y = this.yPos(p.shownPence);
      const clash = placed.some((q) => Math.abs(q.x - x) < 165 && Math.abs(q.y - y) < 24);
      if (clash) continue;
      placed.push({ x, y });
      out.push(p);
      if (out.length >= LABEL_COUNT) break;
    }
    this.labelled = out;
  }

  private niceSqrtStep(maxPounds: number): number {
    // choose a step so we land on ~5 ticks: value_i = i^2 * step
    const target = maxPounds / 25;
    const mags = [1000, 2500, 5000, 10000, 25000, 50000];
    for (const m of mags) if (target <= m) return m;
    return mags[mags.length - 1];
  }

  public xPos(rank: number): number {
    const { w, l, r } = this.box;
    const inner = Math.max(1, w - l - r);
    // the prevalence dataset ranks 1,000 names, so the axis runs 1st to 1000th
    const t = Math.log10(Math.max(1, rank)) / Math.log10(1000);
    return l + t * inner;
  }

  public yPos(pence: number): number {
    const { h, t, b } = this.box;
    const inner = Math.max(1, h - t - b);
    const maxP = Math.max(1, this.plotted.reduce((m, p) => Math.max(m, p.shownPence), 0));
    const frac = Math.sqrt(Math.max(0, pence)) / Math.sqrt(maxP);
    return h - b - frac * inner;
  }

  /** The cloud: canvas, because 3.5k nodes in the DOM is not a chart. */
  private drawCloud(): void {
    const cv = this.cloudRef && this.cloudRef.nativeElement;
    if (!cv) return;
    const { w, h } = this.box;
    const dpr = window.devicePixelRatio || 1;
    cv.width = Math.round(w * dpr);
    cv.height = Math.round(h * dpr);
    cv.style.width = w + 'px';
    cv.style.height = h + 'px';
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    // format-driven plates are drawn hollow: they are still evidence, but the
    // price is not the name's, and a solid dot would say that it was
    for (const p of this.plotted) {
      const x = this.xPos(p.rank);
      const y = this.yPos(p.shownPence);
      ctx.beginPath();
      ctx.arc(x, y, 2, 0, Math.PI * 2);
      if (p.formatValue) {
        ctx.strokeStyle = 'rgba(58,74,80,.30)';
        ctx.lineWidth = 1;
        ctx.stroke();
      } else {
        ctx.fillStyle = 'rgba(58,74,80,.30)';
        ctx.fill();
      }
    }
  }

  /** How many plotted sales are priced by format rather than by the name. */
  public get formatCount(): number {
    return this.plotted.filter((p) => p.formatValue).length;
  }

  /* =============== table =============== */

  public sortBy(k: SortKey): void {
    if (this.sortKey === k) this.sortAsc = !this.sortAsc;
    else {
      this.sortKey = k;
      this.sortAsc = k !== 'price';
    }
  }

  public get sortedRows(): PlottedPoint[] {
    const dir = this.sortAsc ? 1 : -1;
    const rows = this.plotted.slice();
    rows.sort((a, b) => {
      if (this.sortKey === 'rank') return (a.rank - b.rank) * dir;
      if (this.sortKey === 'price') return (a.shownPence - b.shownPence) * dir;
      return a.display.localeCompare(b.display) * dir;
    });
    return rows.slice(0, this.tableRows);
  }

  public showMore(): void {
    this.tableRows += TABLE_PAGE;
  }

  public get hasMoreRows(): boolean {
    return this.tableRows < this.plotted.length;
  }

  /* =============== formatting =============== */

  public money(pence: number): string {
    const p = Math.round(pence / 100);
    if (p >= 1000) return '£' + Math.round(p / 1000) + 'k';
    return '£' + p;
  }

  public moneyFull(pence: number): string {
    return '£' + Math.round(pence / 100).toLocaleString('en-GB');
  }

  public ordinal(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n.toLocaleString('en-GB') + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  public plateLink(reg: string): string[] {
    return ['/plate', reg.replace(/\s+/g, '')];
  }

  public trackByReg(_i: number, p: PlottedPoint): string {
    return p.registration;
  }
}
